// The buildings around the plot, from OpenStreetMap, and what our building
// does to their sun.
//
// Heights come from the `height` tag, else `building:levels` × 3 m plus a
// metre of roof, else an assumed 10 m, and every building says which: an
// assumed height is a guess and the report must not pass it off as a survey.
//
// OMS 119/2014 asks for 1½ h of winter sun not only in our flats but in the
// neighbours' as well. `overshadowing` samples the neighbours' facades that
// face the plot, at ground-floor window height, with and without our
// building, and counts the points our building takes below the line.
import proj4 from "proj4";
import { area, distanceTo, intersection, type MultiPoly, type Pt } from "./geom.ts";
import { solsticeSun, sunHours, type Mass } from "./sun.ts";

/** EPSG:3844, Stereo 70 (Dealul Piscului 1970), with its 7-parameter shift to WGS84. */
export const STEREO70 = "+proj=sterea +lat_0=46 +lon_0=25 +k=0.99975 +x_0=500000 +y_0=500000 +ellps=krass +towgs84=2.329,-147.042,-92.08,0.309,-0.325,-0.497,5.69 +units=m +no_defs";

export const toWgs84 = (east: number, north: number): [number, number] => proj4(STEREO70, "WGS84", [east, north]) as [number, number]; // [lon, lat]
export const toStereo70 = (lon: number, lat: number): [number, number] => proj4("WGS84", STEREO70, [lon, lat]) as [number, number];

export interface Neighbour extends Mass {
  heightFrom: "height" | "levels" | "assumed";
}

export const ASSUMED_HEIGHT = 10;
export const STOREY = 3;

/** The Overpass query for every building within `radius` metres of a plot given in local metres. */
export function overpassQuery(ring: Pt[], origin: Pt, radius = 120): string {
  const xs = ring.map((p) => p[0] + origin[0]), ys = ring.map((p) => p[1] + origin[1]);
  const [w, s] = toWgs84(Math.min(...xs) - radius, Math.min(...ys) - radius);
  const [e, n] = toWgs84(Math.max(...xs) + radius, Math.max(...ys) + radius);
  return `[out:json][timeout:25];(way["building"](${s.toFixed(6)},${w.toFixed(6)},${n.toFixed(6)},${e.toFixed(6)}););out geom tags;`;
}

export interface OsmWay { type: string; tags?: Record<string, string | undefined>; geometry?: { lat: number; lon: number }[] }

/**
 * Buildings from an Overpass response, in the plot's local metres. One that
 * stands on the plot itself is left out: it is what the project replaces, or
 * it is entered as an existing building, not a neighbour.
 */
export function neighboursFrom(json: { elements?: OsmWay[] }, origin: Pt, plot: MultiPoly): Neighbour[] {
  const out: Neighbour[] = [];
  for (const el of json.elements ?? []) {
    if (el.type !== "way" || !el.geometry || el.geometry.length < 4) continue;
    const ring = el.geometry.map(({ lat, lon }) => { const [e, n] = toStereo70(lon, lat); return [e - origin[0], n - origin[1]] as Pt; });
    if (ring.length > 3 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]) ring.pop();
    const footprint: MultiPoly = [[ring]];
    if (area(intersection(footprint, plot)) > 0.5 * area(footprint)) continue;
    const t = el.tags ?? {};
    const h = parseFloat((t.height ?? "").replace(",", "."));
    const lv = parseFloat(t["building:levels"] ?? "");
    const [top, heightFrom] = Number.isFinite(h) && h > 0 ? [h, "height" as const]
      : Number.isFinite(lv) && lv > 0 ? [lv * STOREY + 1, "levels" as const]
      : [ASSUMED_HEIGHT, "assumed" as const];
    out.push({ footprint, base: 0, top, heightFrom });
  }
  return out;
}

export interface Overshadowing {
  points: number;
  /** Facade points that had 1½ h before our building and do not after it. */
  lost: number;
  /** Of those, how many sit on buildings whose height is assumed. */
  lostOnAssumed: number;
  samples: { at: Pt; before: number; after: number }[];
}

/** Sample the facades of neighbours that face the plot, with and without our building. */
export function overshadowing(neighbours: Neighbour[], ours: Mass[], plot: MultiPoly, latitude: number, spacing = 4): Overshadowing {
  const sun = solsticeSun(latitude);
  const pts = plot.flatMap((p) => p[0]);
  const c: Pt = [pts.reduce((t, p) => t + p[0], 0) / pts.length, pts.reduce((t, p) => t + p[1], 0) / pts.length];
  const samples: Overshadowing["samples"] = [];
  let lost = 0, lostOnAssumed = 0;
  neighbours.forEach((nb, k) => {
    if (distanceTo(c, nb.footprint) > 120) return;
    const others = neighbours.filter((_, j) => j !== k);
    const ring = nb.footprint[0][0];
    const ccw = ring.reduce((t, p, i) => { const q = ring[(i + 1) % ring.length]; return t + p[0] * q[1] - q[0] * p[1]; }, 0) > 0;
    ring.forEach((a, i) => {
      const b = ring[(i + 1) % ring.length];
      const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (L < 3) return;
      // outward normal, then keep only the facades that look towards the plot
      let n: Pt = [(b[1] - a[1]) / L, -(b[0] - a[0]) / L];
      if (!ccw) n = [-n[0], -n[1]];
      const m: Pt = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      const toPlot = [c[0] - m[0], c[1] - m[1]];
      if (n[0] * toPlot[0] + n[1] * toPlot[1] <= 0) return;
      for (let s = spacing / 2; s < L; s += spacing) {
        const at: Pt = [a[0] + ((b[0] - a[0]) * s) / L, a[1] + ((b[1] - a[1]) * s) / L];
        const before = sunHours(at, 1.5, n, others, sun);
        const after = sunHours(at, 1.5, n, [...others, ...ours], sun);
        samples.push({ at, before, after });
        if (before >= 1.5 && after < 1.5) { lost++; if (nb.heightFrom === "assumed") lostOnAssumed++; }
      }
    });
  });
  return { points: samples.length, lost, lostOnAssumed, samples };
}
